import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge, TableWithSearch } from "@/components";
import type { DataTableFeatures } from "@/components/data-table/features";
import { orderBadgeVariants } from "@/constants/orders";
import { useBreadCrumbContext } from "@/contexts/breadcrumb";
import { customerFullname } from "@/helpers/customer";
import { formatLongDatetime } from "@/helpers/dates";
import { uuidNumber } from "@/helpers/uuid";
import { useCurrentStore, useOrders, usePaginatedSearch } from "../../hooks";

const Orders = () => {
	const { t } = useTranslation();
	useBreadCrumbContext([{ label: t("orders.pageTitle") }]);
	const currentStore = useCurrentStore();
	const { searchQuery, page, setPage, onSearchQueryChange } =
		usePaginatedSearch();
	const { data, isLoading } = useOrders({
		page,
		// `Order` has no `client` association — the association is `customer`, which
		// is still ransackable, so this predicate was silently dropped.
		"q[customerFirstnameOrCustomerLastnameCont]": searchQuery,
		"q[storeIdEq]": currentStore?.id,
		"q[s]": "created_at desc",
	});

	const columnHelper = createColumnHelper<DataTableFeatures, Order>();

	const columns = useMemo(
		() =>
			[
				columnHelper.accessor("id", {
					header: t("orders.table.id"),
					cell: (props) => (
						<Link className="underline" to={`/orders/${props.getValue()}`}>
							{uuidNumber(props.getValue())}
						</Link>
					),
				}),
				columnHelper.accessor((row) => customerFullname(row.customer), {
					id: "fullname",
					header: t("orders.table.customer"),
					cell: (props) => (
						<Link
							className="underline"
							to={`/customers/${props.row.original.customer.id}/edit`}
						>
							{props.getValue()}
						</Link>
					),
				}),
				columnHelper.accessor("state", {
					header: t("orders.table.state"),
					cell: (props) => (
						<Badge variant={orderBadgeVariants[props.getValue()]}>
							{t(`order.states.${props.getValue()}`)}
						</Badge>
					),
				}),
				columnHelper.accessor("createdAt", {
					header: t("orders.table.createdAt"),
					cell: (props) => formatLongDatetime(props.getValue()),
				}),
				columnHelper.accessor("updatedAt", {
					header: t("orders.table.updatedAt"),
					cell: (props) => formatLongDatetime(props.getValue()),
				}),
			] as ColumnDef<DataTableFeatures, Order>[],
		[t, columnHelper],
	);

	return (
		<TableWithSearch
			searchPlaceholder={t("orders.searchPlaceholder")}
			onSearchQueryChange={onSearchQueryChange}
			searchQuery={searchQuery}
			isLoading={isLoading}
			createUrl="/orders/new"
			createLabel={t("orders.createButtonLabel")}
			columns={columns}
			data={data?.data}
			currentPage={page}
			totalPages={data?.headers["total-pages"]}
			onPageChange={setPage}
		/>
	);
};

export default Orders;
